import { useId } from '@fluentui/react-hooks';
import * as React from 'react';

import { LiveSiteEventSeverity, LiveSiteEventState } from '../../models/status';
import { SvgProps } from '../../models/ui/svg';
import { getNormalizedServiceState, NormalizedServiceState } from '../../utilities/status';
import { isUndefinedOrWhiteSpace } from '../../utilities/strings';
import { isNil } from '../../utilities/typeGuards';

interface StatusIconProps {
    /**
     * Only pertinent to the healthy icon at this time.
     * If invertered, the icon is filled green.
     * If not, the icon is filled white with a green border.
     */
    invert?: boolean;

    /**
     * What's the severity of the event?
     */
    severity: LiveSiteEventSeverity | undefined;

    /**
     *
     */
    state: LiveSiteEventState | undefined;

    /**
     * Override aspects of the SVG icon if needed.
     */
    svgStyles?: SvgProps;
}

const DegradedSvg = (props: SvgProps) => {
    const id = useId('degradedSvg');

    return (
        <svg id={id}
            data-name="Layer 1" xmlns="http://www.w3.org/2000/svg"
            width={props.width}
            height={props.height}
            viewBox={props.viewBox}
            className={props.className}>
            <path d="M8.43589 0.266939C9.41956 -0.277899 10.6476 0.0386868 11.251 0.967237L11.3293 1.09763L18.6696 14.35C18.8444 14.6657 18.9361 15.0206 18.9361 15.3814C18.9361 16.508 18.0609 17.4302 16.9533 17.5051L16.8076 17.51H2.12899C1.76825 17.51 1.41343 17.4183 1.09785 17.2436C0.114115 16.6989 -0.269071 15.49 0.197853 14.4858L0.266817 14.3503L7.60508 1.09785C7.79857 0.748414 8.08648 0.460472 8.43589 0.266939Z" fill="#605E5C"/>
            <path d="M8.51849 13.869C8.51849 13.1979 9.10094 12.6538 9.81942 12.6538C10.5379 12.6538 11.1204 13.1979 11.1204 13.869C11.1204 14.5401 10.5379 15.0842 9.81942 15.0842C9.10094 15.0842 8.51849 14.5401 8.51849 13.869Z" fill="white"/>
            <path d="M8.52311 4.85951C8.59813 4.25432 9.14855 3.7844 9.81661 3.78418C10.4847 3.78396 11.0354 4.2535 11.1109 4.85864L11.1198 5.00054L11.1209 7.17383L11.1221 10.2164L11.1134 10.3583C11.0384 10.9634 10.4879 11.4334 9.81989 11.4336C9.15183 11.4338 8.60105 10.9643 8.52557 10.3591L8.51675 10.2172L8.5144 5.00141L8.52311 4.85951Z" fill="white"/>
            <path d="M8.43589 0.266939C9.41956 -0.277899 10.6476 0.0386868 11.251 0.967237L11.3293 1.09763L18.6696 14.35C18.8444 14.6657 18.9361 15.0206 18.9361 15.3814C18.9361 16.508 18.0609 17.4302 16.9533 17.5051L16.8076 17.51H2.12899C1.76825 17.51 1.41343 17.4183 1.09785 17.2436C0.114115 16.6989 -0.269071 15.49 0.197853 14.4858L0.266817 14.3503L7.60508 1.09785C7.79857 0.748414 8.08648 0.460472 8.43589 0.266939Z" fill="#FFBA08"/>
            <path d="M8.51849 13.869C8.51849 13.1979 9.10094 12.6538 9.81942 12.6538C10.5379 12.6538 11.1204 13.1979 11.1204 13.869C11.1204 14.5401 10.5379 15.0842 9.81942 15.0842C9.10094 15.0842 8.51849 14.5401 8.51849 13.869Z" fill="#323130"/>
            <path d="M8.52311 4.85951C8.59813 4.25432 9.14855 3.7844 9.81661 3.78418C10.4847 3.78396 11.0354 4.2535 11.1109 4.85864L11.1198 5.00054L11.1209 7.17383L11.1221 10.2164L11.1134 10.3583C11.0384 10.9634 10.4879 11.4334 9.81989 11.4336C9.15183 11.4338 8.60105 10.9643 8.52557 10.3591L8.51675 10.2172L8.5144 5.00141L8.52311 4.85951Z" fill="#323130"/>
        </svg>
    );
};

const HealthySvg = (props: SvgProps) => {
    const id = useId('healthySvg');

    return (
        <svg id={id}
            data-name="Layer 1" xmlns="http://www.w3.org/2000/svg"
            width={props.width}
            height={props.height}
            viewBox={props.viewBox}
            className={props.className}>
            <circle cx="10" cy="9.5" r="8.5" fill="transparent" stroke="#107C10" strokeWidth="2"/>
            <path d="M6 8.5L8.94737 12L14 6" fill="transparent" stroke="#107C10" strokeWidth="2"/>
        </svg>
    );
};

const InfoSvg = (props: SvgProps) => {
    const id = useId('infoSvg');

    return (
        <svg id={id}
            data-name="Layer 1" xmlns="http://www.w3.org/2000/svg"
            width={props.width}
            height={props.height}
            viewBox={props.viewBox}
            className={props.className}>
            <circle cx="10.5" cy="10.5" r="10" fill="#0067b7" />
            <g>
                <path d="M10.5,6.06A1.5,1.5,0,1,0,9,4.56,1.5,1.5,0,0,0,10.5,6.06Z" fill="#fff" />
                <path d="M10.5,17.94A1.53,1.53,0,0,0,12,16.55V8.32a1.49,1.49,0,0,0-3,0v8.23A1.53,1.53,0,0,0,10.5,17.94Z" fill="#fff" />
            </g>
        </svg>
    );
};

const UnhealthySvg = (props: SvgProps) => {
    const id = useId('unhealthySvg');

    return (
        <svg id={id}
            data-name="Layer 1" xmlns="http://www.w3.org/2000/svg"
            width={props.width}
            height={props.height}
            viewBox={props.viewBox}
            className={props.className}>
            <circle cx="10.5" cy="10.5" r="10" fill="#da3b01" />
            <g>
                <path d="M15.09,5.9A1.66,1.66,0,0,0,12.88,6l-.16.15L6.16,12.72,6,12.89a1.65,1.65,0,0,0-.1,2.21A1.66,1.66,0,0,0,8.12,15l.16-.15,6.56-6.56L15,8.11A1.65,1.65,0,0,0,15.09,5.9Z" fill="#fff" />
                <path d="M5.9,5.91A1.66,1.66,0,0,0,6,8.12l.15.16,6.56,6.56.17.15a1.65,1.65,0,0,0,2.21.1A1.66,1.66,0,0,0,15,12.88l-.15-.16L8.28,6.16,8.11,6A1.65,1.65,0,0,0,5.9,5.91Z" fill="#fff" />
            </g>
        </svg>
    );
};

const NotApplicableSvg = (props: SvgProps) => {
    const id = useId('notAppSvg');

    return (
        <svg id={id}
            data-name="Layer 1" xmlns="http://www.w3.org/2000/svg"
            width={props.width}
            height={props.height}
            viewBox={props.viewBox}
            className={props.className}>
            <path d="M16,10.5A1.53,1.53,0,0,0,14.61,9H6.39a1.49,1.49,0,0,0,0,3h8.22A1.53,1.53,0,0,0,16,10.5Z" fill="#605e5c" stroke="#605e5c" strokeWidth="0.25" />
            <path d="M10.5,20.5a10,10,0,1,1,10-10A10,10,0,0,1,10.5,20.5Zm0-18.18a8.18,8.18,0,1,0,8.18,8.18A8.19,8.19,0,0,0,10.5,2.32Z" fill="#605e5c" stroke="#605e5c" strokeWidth="0.25" />
        </svg>

    );
};

/**
 * Convert a severity to an icon based upon severity and regional availability.
 * @param props
 */
export const StatusIcon = (props: StatusIconProps) => {

    // normalize the svg styles
    const svgStyles: SvgProps = isNil(props.svgStyles) ? {} : props.svgStyles;

    if (isUndefinedOrWhiteSpace(svgStyles.className)) {
        svgStyles.className = 'md-status-icon';
    }

    if (isUndefinedOrWhiteSpace(svgStyles.viewBox)) {
        svgStyles.viewBox = '0 0 21 21';
    }

    if (isNil(svgStyles.height)) {
        svgStyles.height = 21;
    }

    if (isNil(svgStyles.width)) {
        svgStyles.width = 21;
    }

    switch (getNormalizedServiceState(props.state, props.severity)) {
        case NormalizedServiceState.Advisory:
            return <InfoSvg {...svgStyles} />;
        case NormalizedServiceState.Degraded:
            return <DegradedSvg {...svgStyles} />;
        case NormalizedServiceState.Healthy:
            if (props.invert) {
                return <HealthySvg {...svgStyles} className={'invert-healthy'} />;
            }
            return <HealthySvg {...svgStyles} />;
        case NormalizedServiceState.NotApplicable:
            return <NotApplicableSvg {...svgStyles} />;
        case NormalizedServiceState.Unhealthy:
            return <UnhealthySvg {...svgStyles} />;
        case NormalizedServiceState.Unrecognized:
            return <NotApplicableSvg {...svgStyles} />;
    }
};